/**
 * The Partner dashboard: /partner
 *
 * What somebody sees about the people they introduced and the money they are
 * owed. One tRPC call draws the whole page — see the note on `partner.me` for
 * why it is one call and not five.
 *
 * ── EVERY FIGURE IS EXPLAINED ON SCREEN ─────────────────────────────────────
 *
 * A dashboard about money that a person is paid from has one job beyond being
 * correct: nobody should ever have to email and ask what a number means. So
 * "pending" says why it is pending and when it stops being, every commission
 * line shows the sale and the rate behind it, and the totals add up in the
 * order they are read. If a figure here ever needs a footnote that is not on
 * the page, the page is wrong.
 *
 * Amounts arrive as integer cents and are formatted once, here.
 */

import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/core/hooks/useAuth";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { BUTTON } from "@/lib/buttonStyles";
import { Loader2 } from "lucide-react";

/** Cents to "$58.80". The only place money becomes a string on this page. */
function money(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Where a Partner can send people. Paths, not full URLs, so the origin is
 * written once below.
 *
 * The checkout paths mirror CHECKOUT_PAGES in the homepage repo's config.js.
 * A renamed route there leaves a dead link here — worth a grep if one ever
 * moves, and the reason the homepage is the only place they are authored.
 */
const LINK_TARGETS = [
  /* First because it is the one to use in ads and social posts: a free
     sign-up page with no navigation, then the $19 offer. The homepage
     repo's /join/ funnel. */
  { label: "Free sign-up page (best for ads and social posts)", path: "/join/" },
  { label: "The homepage", path: "/" },
  { label: "Pricing", path: "/#pricing" },
  { label: "The app store", path: "/#store" },
  { label: "Smart Stack + membership", path: "/checkout/plus/" },
  { label: "Claude app", path: "/checkout/claude/" },
  { label: "ChatGPT app", path: "/checkout/chatgpt/" },
  { label: "Perplexity app", path: "/checkout/perplexity/" },
  { label: "Gemini app", path: "/checkout/gemini/" },
  { label: "Gemini Notebook app", path: "/checkout/gemini-notebook/" },
];

/**
 * Build one link, putting the code BEFORE any fragment.
 *
 * "/#pricing" + "?ref=code" gives "/#pricing?ref=code", where the query
 * string is part of the fragment: it never reaches the server, never appears
 * in location.search, and the link tracks nobody while looking perfectly
 * correct. Two of the targets above are exactly that shape, and this
 * caught us once already in assets/js/partner.js on the other side.
 */
function linkFor(path: string, code: string): string {
  const origin = "https://yoursmartstack.com";
  const hash = path.indexOf("#");
  const base = hash === -1 ? path : path.slice(0, hash);
  const fragment = hash === -1 ? "" : path.slice(hash);
  const sep = base.indexOf("?") === -1 ? "?" : "&";
  return `${origin}${base}${sep}ref=${encodeURIComponent(code)}${fragment}`;
}

const STANDING: Record<string, { label: string; className: string }> = {
  member: { label: "Smart Stack +", className: "bg-primary/10 text-primary" },
  buyer: { label: "App owner", className: "bg-[color:var(--manus)]/10 text-[color:var(--manus)]" },
  free: { label: "Free", className: "bg-muted text-muted-foreground" },
};

export default function PartnerDashboard() {
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!loading && !user) setLocation("/login?returnTo=%2Fpartner");
  }, [loading, user, setLocation]);

  const query = trpc.partner.me.useQuery(undefined, { enabled: !loading && !!user });
  /* Commissions first: it is what a Partner opens this page to check. The
     links are a thing you fetch once and paste somewhere, not something you
     come back to read. */
  const [tab, setTab] = useState<"commissions" | "links">("commissions");

  if (loading || (user && query.isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin w-8 h-8 text-muted-foreground" />
      </div>
    );
  }
  if (!user) return null;

  /* Not a Partner, or suspended. The server's message is written for the
     person reading it and says what to do next, so it is shown as-is rather
     than replaced with something generic. */
  if (query.error) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader context="Partner" showPrimaryAction={false} />
        <main className="flex-1">
          <div className="container max-w-2xl py-16">
            <h1 className="font-['Fraunces'] text-3xl font-semibold text-foreground">
              Partner dashboard
            </h1>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">
              {query.error.message}
            </p>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const d = query.data;
  if (!d) return null;

  const rate = (d.partner.commissionBps / 100).toFixed(
    d.partner.commissionBps % 100 === 0 ? 0 : 1
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader context="Partner" showPrimaryAction={false} />

      <main className="flex-1">
        <div className="container max-w-5xl py-8 sm:py-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            Partner
          </p>
          <h1 className="mt-3 font-['Fraunces'] text-3xl sm:text-4xl font-semibold text-foreground leading-tight tracking-tight">
            {d.partner.name}
          </h1>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-2xl">
            You earn {rate}% of everything the people below buy
            {d.partner.tier2Bps > 0
              ? `, and ${(d.partner.tier2Bps / 100).toFixed(
                  d.partner.tier2Bps % 100 === 0 ? 0 : 1
                )}% of what the Partners you introduced sell`
              : ""}
            .
          </p>

          <div className="mt-8 flex gap-1 border-b border-border" role="tablist" aria-label="Partner sections">
            {(["commissions", "links"] as const).map(t => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                className={
                  "min-h-11 px-4 text-base font-semibold border-b-2 -mb-px transition-colors " +
                  (tab === t
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground")
                }
              >
                {t === "commissions" ? "Commissions" : "Links"}
              </button>
            ))}
          </div>

          {tab === "links" ? (
            <>
          {/* ── Their links ─────────────────────────────────────────────
              Moved here from the public /partners/ page in Sep 2026. It was
              on the sales page with a "paste your code" box, which meant the
              one tool a Partner actually uses sat behind a form on a page
              written to persuade them to apply — and anybody could type any
              code into it. Here the code is known, because they are signed in
              as the Partner it belongs to.  */}
          <div className="mt-8 rounded-lg border border-border bg-card p-6">
            <h2 className="font-['Fraunces'] text-2xl font-semibold text-foreground">
              Your links
            </h2>
            <p className="mt-2 text-base text-muted-foreground leading-relaxed max-w-2xl">
              Any of these counts the same way. Send whichever page suits what you
              are writing about &mdash; somebody who lands on one and buys a
              different thing entirely is still your sale.
            </p>

            <ul className="mt-5 space-y-3">
              {LINK_TARGETS.map(t => (
                <PartnerLink key={t.path} label={t.label} url={linkFor(t.path, d.partner.code)} />
              ))}
            </ul>

            <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
              Or put <code className="rounded bg-muted px-1.5 py-0.5">?ref={d.partner.code}</code>{" "}
              on the end of any page on yoursmartstack.com yourself. If the address
              already has a <code className="rounded bg-muted px-1.5 py-0.5">?</code> in
              it, use <code className="rounded bg-muted px-1.5 py-0.5">&amp;</code> instead.
            </p>
          </div>
            </>
          ) : (
            <>
          {/* ── The four numbers ────────────────────────────────────── */}
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              label="Free members"
              value={String(d.counts.free)}
              sub="Joined through you, not paying yet"
            />
            <Tile
              label="Plus members"
              value={String(d.counts.members)}
              sub="On the $19 membership right now"
            />
            <Tile
              label="Apps sold"
              value={String(d.counts.appsSold)}
              sub="One-time purchases, counted per sale"
            />
            <Tile
              label="Pending commissions"
              value={money(d.totals.pending)}
              sub={`Earned, waiting out the ${d.refundWindowDays}-day refund window`}
            />
          </div>

          {/* ── And what that is worth ───────────────────────────────────── */}
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Tile
              label="Ready to pay"
              value={money(d.totals.payable)}
              sub="On your next payment"
            />
            <Tile
              label={d.totals.inTransit > 0 ? "On its way" : "Paid to you"}
              value={money(d.totals.inTransit > 0 ? d.totals.inTransit : d.totals.paid)}
              sub={
                d.totals.inTransit > 0
                  ? `Approved and being sent. ${money(d.totals.paid)} paid before this.`
                  : "Money that has actually reached you"
              }
            />
            <Tile
              label="Earned in total"
              value={money(d.totals.earned)}
              sub={
                d.totals.tier2 > 0
                  ? `Includes ${money(d.totals.tier2)} from Partners you introduced`
                  : "Since you joined"
              }
            />
          </div>

          {/* ── Their people ───────────────────────────────────────────── */}
          <section className="mt-12">
            <h2 className="font-['Fraunces'] text-2xl font-semibold text-foreground">
              Your people
            </h2>
            <p className="mt-2 text-base text-muted-foreground leading-relaxed max-w-2xl">
              {d.counts.total === 0
                ? "Nobody has come through your link yet."
                : `${d.counts.total} ${d.counts.total === 1 ? "person" : "people"} — ` +
                  `${d.counts.members} on Smart Stack +, ` +
                  `${d.counts.buyers} who ${d.counts.buyers === 1 ? "owns" : "own"} an app, ` +
                  `${d.counts.free} on the free plan. Everyone is counted once.`}
            </p>

            {d.referrals.length > 0 && (
              <div className="mt-5 overflow-x-auto rounded-lg border border-border bg-card">
                <table className="w-full text-base">
                  <thead>
                    <tr className="bg-muted text-left">
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Joined</Th>
                      <Th>Standing</Th>
                      <Th right>Apps</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.referrals.map(r => {
                      const s = STANDING[r.standing] ?? STANDING.free;
                      return (
                        <tr key={r.userId} className="border-t border-border">
                          <Td>{r.name || "—"}</Td>
                          <Td className="break-all text-muted-foreground">{r.email}</Td>
                          <Td className="whitespace-nowrap">{day(r.joinedAt)}</Td>
                          <Td>
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-sm font-semibold ${s.className}`}
                            >
                              {s.label}
                            </span>
                          </Td>
                          <Td right>{r.apps || "—"}</Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Every commission ───────────────────────────────────────── */}
          <section className="mt-12">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="font-['Fraunces'] text-2xl font-semibold text-foreground">
                What you have earned
              </h2>
              {d.commissions.length > 0 && (
                <button
                  type="button"
                  className={BUTTON.secondarySm}
                  onClick={() => downloadReport(d)}
                >
                  Download report
                </button>
              )}
            </div>
            {d.commissions.length === 0 ? (
              <p className="mt-2 text-base text-muted-foreground">
                Nothing yet. Commissions appear here the moment a sale goes through.
              </p>
            ) : (
              <div className="mt-5 overflow-x-auto rounded-lg border border-border bg-card">
                <table className="w-full text-base">
                  <thead>
                    <tr className="bg-muted text-left">
                      <Th>Date</Th>
                      <Th>Who</Th>
                      <Th>What</Th>
                      <Th right>Sale</Th>
                      <Th right>Rate</Th>
                      <Th right>You earned</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.commissions.map(c => (
                      <tr key={c.id} className="border-t border-border">
                        <Td className="whitespace-nowrap">{day(c.earnedAt)}</Td>
                        <Td>
                          {c.customerName || "—"}
                          <span className="block text-sm text-muted-foreground break-all">
                            {c.customerEmail}
                          </span>
                        </Td>
                        <Td>
                          {c.what}
                          {c.tier === 2 && c.via ? (
                            <span className="block text-sm text-muted-foreground">
                              Second tier — sold by {c.via}
                            </span>
                          ) : null}
                        </Td>
                        <Td right className="tabular-nums">
                          {money(c.baseAmount)}
                        </Td>
                        <Td right className="tabular-nums text-muted-foreground">
                          {(c.rateBps / 100).toFixed(c.rateBps % 100 === 0 ? 0 : 1)}%
                        </Td>
                        <Td right className="tabular-nums font-semibold">
                          {money(c.amount)}
                        </Td>
                        <Td>
                          {c.status === "reversed" ? (
                            <span className="text-muted-foreground">Refunded</span>
                          ) : c.status === "paid" ? (
                            <span className="text-muted-foreground">Paid</span>
                          ) : c.pending ? (
                            <span className="text-muted-foreground">Settling</span>
                          ) : (
                            <span className="font-semibold text-foreground">Ready</span>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Payments to them ───────────────────────────────────────── */}
          {d.payouts.length > 0 && (
            <section className="mt-12">
              <h2 className="font-['Fraunces'] text-2xl font-semibold text-foreground">
                Payments to you
              </h2>
              <div className="mt-5 overflow-x-auto rounded-lg border border-border bg-card">
                <table className="w-full text-base">
                  <thead>
                    <tr className="bg-muted text-left">
                      <Th>Date</Th>
                      <Th right>Amount</Th>
                      <Th>Status</Th>
                      <Th>Reference</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.payouts.map(p => (
                      <tr key={p.id} className="border-t border-border">
                        <Td className="whitespace-nowrap">{day(p.paidAt ?? p.createdAt)}</Td>
                        <Td right className="tabular-nums font-semibold">
                          {money(p.amount)}
                        </Td>
                        <Td>{p.status === "paid" ? "Paid" : "On its way"}</Td>
                        <Td className="text-muted-foreground">{p.reference || "—"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
            </>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

/**
 * One link, selectable and copyable.
 *
 * A read-only input rather than a <code> block: it selects on tap, which is
 * what somebody on a phone does before they find a copy button, and it stays
 * usable if the clipboard call is refused. readOnly rather than disabled so
 * it can still be focused.
 */
function PartnerLink({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <li className="grid gap-2 sm:grid-cols-[190px_minmax(0,1fr)_auto] sm:items-center sm:gap-3">
      <span className="text-base font-semibold text-foreground">{label}</span>
      <input
        readOnly
        value={url}
        onFocus={e => e.currentTarget.select()}
        aria-label={`${label} Partner link`}
        className="min-w-0 rounded-md border border-border bg-muted px-3 py-2.5 font-mono text-sm text-foreground"
      />
      <button
        type="button"
        className={BUTTON.secondarySm}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          } catch {
            /* Clipboard refused — http://, or a browser that asks. The field
               is right there and already selectable, so say nothing and let
               them copy it by hand rather than pop an error at them. */
          }
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </li>
  );
}

/**
 * The report a Partner can pull for themselves.
 *
 * One row per commission, carrying who bought, what, for how much, at what
 * rate, and what it earned them — which is the whole question somebody asks
 * when they want to check a statement.
 *
 * Built from data the page has already loaded and saved with a blob URL, so
 * nothing is uploaded and no second endpoint exists that could return one
 * Partner another Partner's rows.
 */
function downloadReport(d: {
  partner: { code: string };
  commissions: Array<{
    earnedAt: string;
    customerName: string;
    customerEmail: string;
    what: string;
    baseAmount: number;
    rateBps: number;
    amount: number;
    status: string;
    pending: boolean;
    tier: number;
  }>;
}) {
  const headers = [
    "date", "customer", "email", "what", "sale_usd",
    "rate_percent", "commission_usd", "tier", "status",
  ];
  /* Quote every field: names and product titles contain commas, and one
     unquoted comma shifts every later column by one. */
  const cell = (v: string | number) => `"${String(v ?? "").replace(/"/g, '""')}"`;

  const rows = d.commissions.map(c =>
    [
      c.earnedAt.slice(0, 10),
      c.customerName,
      c.customerEmail,
      c.what,
      (c.baseAmount / 100).toFixed(2),
      (c.rateBps / 100).toFixed(c.rateBps % 100 === 0 ? 0 : 1),
      (c.amount / 100).toFixed(2),
      c.tier,
      c.status === "reversed"
        ? "refunded"
        : c.status === "paid"
          ? "paid"
          : c.pending
            ? "settling"
            : "ready",
    ].map(cell).join(",")
  );

  const csv = [headers.map(cell).join(","), ...rows].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${d.partner.code}-commissions-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  // Next tick: Safari has not always finished reading the blob when click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      <div className="mt-2 font-['Fraunces'] text-3xl font-semibold text-foreground tabular-nums">
        {value}
      </div>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{sub}</p>
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      scope="col"
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap ${
        right ? "text-right" : ""
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  right,
  className = "",
}: {
  children: React.ReactNode;
  right?: boolean;
  className?: string;
}) {
  return <td className={`px-4 py-3 ${right ? "text-right" : ""} ${className}`}>{children}</td>;
}
